import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import RiskScoreCard from './components/RiskScoreCard';
import RiskChecklist from './components/RiskChecklist';
import AIRiskSummary from './components/AIRiskSummary';
import RedFlagDetector from './components/RedFlagDetector';

const mockUser = { id: 1, name: 'Sarah Mitchell', email: 'sarah@email.com', role: 'user' };

const RiskAssessment = () => {
  const navigate = useNavigate();
  const [propertyUrl, setPropertyUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleAnalyze = () => {
    if (!propertyUrl?.trim()) return;
    setIsAnalyzing(true);
    setTimeout(() => setIsAnalyzing(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={mockUser} onLogout={() => navigate('/authentication')} />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-8">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 text-sm">
              <Icon name="ArrowLeft" size={16} color="currentColor" />
              Back to Home
            </button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,68,68,0.15)', border: '1px solid rgba(255,68,68,0.3)' }}>
                <Icon name="AlertTriangle" size={20} color="#FF4444" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Risk Identification</h1>
            </div>
            <p className="text-muted-foreground">Automatically detect potential issues, red flags, and risks before making an offer on any property.</p>
          </div>

          <div className="bg-card rounded-xl border border-border p-6 mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4">Assess Property Risks</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <input type="text" value={propertyUrl} onChange={e => setPropertyUrl(e?.target?.value)} placeholder="Paste Domain or realestate.com.au listing URL..." className="flex-1 px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-red-400 transition-colors" />
              <button onClick={handleAnalyze} disabled={isAnalyzing || !propertyUrl?.trim()} className="px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #FF4444, #CC2222)', color: '#fff', boxShadow: '0 0 20px rgba(255,68,68,0.3)' }}>
                {isAnalyzing ? 'Scanning...' : 'Scan for Risks'}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Or explore the sample risk assessment below.</p>
          </div>

          <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3">
            <Icon name="Info" size={18} color="#FF6666" />
            <div>
              <p className="text-sm font-medium text-foreground">Sample Assessment: 42 Harbour View Terrace, Mosman NSW 2088</p>
              <p className="text-xs text-muted-foreground">4 risks flagged — review before making an offer</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-1"><RiskScoreCard score={42} /></div>
            <div className="lg:col-span-2"><RedFlagDetector /></div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-4">Full Risk Checklist</h2>
            <RiskChecklist />
          </div>

          <div className="mb-6">
            <AIRiskSummary />
          </div>

          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-3">Next Steps</h3>
            <p className="text-muted-foreground text-sm mb-4">Based on the risk assessment, we recommend the following actions before proceeding.</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/agent-questions')} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' }}>Get Agent Questions →</button>
              <button onClick={() => navigate('/offer-range-guidance')} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(0,255,136,0.1)', color: '#00FF88', border: '1px solid rgba(0,255,136,0.3)' }}>Calculate Offer Range →</button>
              <button onClick={() => navigate('/reports')} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(170,136,255,0.1)', color: '#AA88FF', border: '1px solid rgba(170,136,255,0.3)' }}>Save Report →</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RiskAssessment;
